import arrowDownIcon from "@loot-os/icons/icons/arrow-down";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { IconButton } from "../../core/IconButton.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import { useLayout } from "../../foundations/themeContext.js";
import type { ChatRowStyle, ChatSurface } from "./chatTypes.js";

/** How close to the bottom still counts as reading the newest messages. */
const PINNED_SLACK_PX = 40;

export interface ChatTranscriptProps {
  /** The log's name, e.g. "Trainer chat messages". */
  label: string;
  /** The rows, oldest first: ChatMessage, ChatPullMessage, ChatSystemMessage. */
  children: ReactNode;
  /** The button back to the newest message, shown once the reader scrolls up. */
  jumpLabel: string;
  /**
   * Reads new messages out as they arrive. Off by default: a busy room would talk over
   * everything else; the log can still be read at the reader's pace.
   */
  announce?: boolean;
  /** Overrides the theme's row style, for the gap between rows. */
  rowStyle?: ChatRowStyle;
  /** `overlay` fades the oldest rows out at the top, over a stream or room art. */
  surface?: ChatSurface;
  className?: string;
}

/**
 * The scrolling list of a chat. It stays on the newest message while the reader is there, holds
 * their place when they scroll up to read, and offers a way back down.
 */
export function ChatTranscript({
  label,
  children,
  jumpLabel,
  announce = false,
  rowStyle,
  surface = "panel",
  className,
}: ChatTranscriptProps) {
  const layout = useLayout();
  const style = rowStyle ?? layout.chatRows;
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);
  const [pinned, setPinned] = useState(true);

  // Whenever the rows change size (a message arrives, an image loads), follow them down, but
  // only while the reader is at the bottom.
  useEffect(() => {
    const scroller = scrollRef.current;
    const list = listRef.current;
    if (!scroller || !list) return;
    const follow = () => {
      if (pinnedRef.current) scroller.scrollTop = scroller.scrollHeight;
    };
    follow();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(follow);
    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < PINNED_SLACK_PX;
    pinnedRef.current = atBottom;
    setPinned(atBottom);
  };

  const jump = () => {
    const el = scrollRef.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ top: el.scrollHeight, behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <div className={cn("relative flex min-h-0 flex-1 flex-col", className)}>
      <div
        ref={scrollRef}
        role="log"
        aria-label={label}
        aria-live={announce ? "polite" : "off"}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable (WCAG 2.1.1).
        tabIndex={0}
        onScroll={onScroll}
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pt-4 pb-2 [scrollbar-width:thin]",
          FOCUS_RING,
          surface === "overlay" && "[mask-image:linear-gradient(to_bottom,transparent,black_80px)]",
        )}
      >
        <div
          ref={listRef}
          className={cn("flex flex-col", style === "stream" ? "gap-0.5" : "gap-2")}
        >
          {children}
        </div>
      </div>
      {!pinned && (
        <IconButton
          icon={arrowDownIcon}
          label={jumpLabel}
          variant="glass"
          size="sm"
          onClick={jump}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-pill shadow-float"
        />
      )}
    </div>
  );
}
